<template>
  <p v-if="sequence?.sequence_type == SequenceType.AutomaticProgressive">
    Esta acción activa una llamada automática al contacto utilizando los últimos datos
    proporcionados. Ideal para iniciar el contacto de manera rápida y efectiva.
  </p>
  <p v-else>
    Esta acción activa una llamada automática al contacto utilizando los últimos datos
    proporcionados. Se transferirá a un usuario únicamente si la llamada reconoce que el contacto ha
    contestado. Caso contrario, se continuará con la secuencia.
  </p>

  <a-form
    layout="vertical"
    ref="formRef"
    :model="formState"
    v-if="sequence?.sequence_type == SequenceType.Multichannel"
  >
    <!-- <a-form-item name="waiting_time" label="Tiempo de espera (hh:mm)" required>
      <a-input v-model:value="formState.waiting_time" placeholder="03:30" />
    </a-form-item> -->
    <ActionPicker
      v-if="formState.action"
      v-model:value="formState.action"
      :isMultichannel="true"
      :disabled="disabled"
      :sequence="sequence"
      @update:value="changeDisabledButton"
    />
  </a-form>

  <h2>Redirección cuando el contacto conteste</h2>

  <p class="info">Se derivará a los usuarios asignados a esta secuencia.</p>

  <agents-picker
    v-if="sequence?.sequence_type == SequenceType.Multichannel"
    :value="formState.users"
    @update:value="formState.users = $event; changeDisabledButton();"
    :items="users"
    :disabled="disabled"
  />
  <!-- :items="advancedFiltersData.users || []" -->

  <a-alert
    v-if="sequence?.sequence_type == SequenceType.AutomaticProgressive"
    class="automatic_call_alert"
    type="warning"
    show-icon
  >
    <template #message>
      Recuerda que si se tipifica con un valor diferente a “Contacto Directo” sin resolver el
      ticket, no se llamará a los teléfonos restantes del contacto y continuará con el siguiente
      contacto.
      <a-button class="automatic_call_alert__link" type="link" @click="handleViewCriteria">
        Ver criterios
      </a-button>
    </template>
  </a-alert>
</template>

<script setup lang="ts">
import { ref, onMounted, inject, Ref, watch } from "vue";
import { FormInstance } from "ant-design-vue";

import { Sequence, SequenceNode } from "@/@types/flows/sequences";
import { SequenceType } from "@/app/flows/sequences/utils/enums";
import ActionPicker from "@/app/flows/sequences/components/pickers/ActionPicker.vue";
import AgentsPicker from "@/app/shared/components/pickers/AgentsPicker.vue";
import fetchPortfolioUsers from "@/app/queues/services/requests/fetchAgentsQueue";
import EventBus from "@/app/shared/utils/eventBus";
import { OPEN_HELP_CENTER_EVENT } from "@/app/entities/utils/helpCenter";
import { isEqual } from "lodash";

// Ruta de navegación dentro del Centro de ayuda de Secuencias:
// [order del item de menú, order del subitem]. Ajustar a los orders definitivos
// cuando se cargue el contenido de `tab_sequences`.
const CRITERIA_HELP_ITEM_ORDERS = [1, 1];

const props = defineProps<{ node: any; sequence: Sequence; disabled: boolean }>();

const saveDisabled = inject("saveDisabled") as Ref<Boolean>;
const users = ref([]);
const formRef = ref<FormInstance>();
const formState = ref<Partial<SequenceNode>>({
  action: props.node?.action,
  users: props.node?.users
    ? users.value.filter((user: any) => props.node?.users.includes(user.id))
    : [],
});

watch(
  () => props.node,
  () => {
    formState.value = {
      action: props.node?.action,
      users: props.node?.users
        ? users.value.filter((user: any) => props.node?.users.includes(user.id))
        : [],
    };
  }
)

const changeDisabledButton = () => {
  saveDisabled.value = false;
};

const handleViewCriteria = () => {
  EventBus.emit(OPEN_HELP_CENTER_EVENT, {
    scope: "tab_sequences",
    itemOrders: CRITERIA_HELP_ITEM_ORDERS,
  });
};

const fetchUsers = async () => {
  if (!props.sequence?.portfolio?.id) return;

  // loading.value = true;

  const response = await fetchPortfolioUsers(props.sequence?.portfolio?.id, { sequences: true });
  users.value = response;
  formState.value.users = users.value.filter((user: any) => props.node?.users.includes(user.id));

  // loading.value = false;
};

onMounted(async () => {
  await fetchUsers();
});

const getValidatedFormState = async () => {
  await formRef.value?.validateFields();
  const formStateAux = {
    ...formState.value,
    users: formState.value.users?.map((el: any) => el.id),
  };
  return formStateAux;
};

watch(
  () => formState.value,
  (lastValue, newValue) => {
    if (lastValue && newValue) {
      const firstUsers = users.value.filter((user: any) => props.node?.users.includes(user.id));
      if (!isEqual(firstUsers, formState.value.users) || formState.value.action != props.node?.action)
        saveDisabled.value = false;
      else
        saveDisabled.value = true;
    }
  },
  { deep: true, immediate: true }
)

defineExpose({ getValidatedFormState });
</script>

<style scoped lang="sass">
.automatic_call_alert
  // Anclado al fondo del drawer, sin taparlo si el contenido crece
  margin-top: auto
  strong
    font-weight: 600
  &__link
    padding: 0
    height: auto

p
  color: $text-primary
  margin-bottom: 24px

h2
  color: $text-primary
  font-size: 16px
  font-weight: 600
  margin-bottom: 12px

.info
  color: $gray-7
</style>
